/**
 * showdownMoves.batch5 — batch 5 of the per-move Showdown animation ports.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `actor.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Where a move pointed at a shared `BattleOtherAnims` template we either
 * reuse the ported template from {@link module:showdownOther} (when it is already
 * exported there) or port the template locally in this file.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown referenced an effect we did not vendor (notably `attacker.sp` /
 * `defender.sp` — the combatant's own sprite drawn as an fx, which our engine has
 * no equivalent for), the call is omitted (same convention as `showdownOther`'s
 * `fastattack`) and noted inline; the rest of the choreography is preserved. Remote
 * background images (`bg-space.jpg`, `weather-sunnyday.jpg`) are substituted with a
 * `backgroundEffect` colour wash; CSS `linear-gradient(...)` washes use their first
 * stop colour. The motion is always preserved verbatim.
 *
 * Test/runtime separation: imports ONLY TYPES from `showdownFx.ts` (it pulls in
 * Phaser, which can't load in the node test env); every script is a pure function
 * over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, punchattack, bite as biteTemplate } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims` entries this batch delegates to
 * that are not exported from showdownOther.ts).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.drain` — three energy orbs siphon defender → attacker. */
const drain: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 0 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 500, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 50 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 550, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 100 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 600, opacity: 0 },
    'ballistic2Under',
  );
};

/** PS `BattleOtherAnims.clawattack` — a two-swipe lunge leaving four claw marks. */
const clawattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.sound` — three concentric wisp rings expand off the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/**
 * PS `BattleOtherAnims.consume` — a wisp is drawn into the user, who bobs as it
 * heals. PS passes a single combatant `[defender]`; we read the move's defender.
 */
const consume: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    {
      x: defender.leftof(-25),
      y: defender.y + 40,
      z: defender.behind(-20),
      scale: 0.5,
      opacity: 1,
    },
    {
      x: defender.leftof(-15),
      y: defender.y + 35,
      z: defender.z,
      scale: 0,
      opacity: 0.2,
      time: 500,
    },
    'swing',
    'fade',
  );

  defender.delay(400);
  defender.anim({ y: defender.y + 5, yscale: 1.1, time: 200 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ y: defender.y + 5, yscale: 1.1, time: 200 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
};

/** PS `BattleOtherAnims.dance` — a small sway of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.xattack` — two wisp bursts + a double-swipe lunge. */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/** PS `BattleOtherAnims.kick` — a foot stamp on the defender. */
const kick: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x, y: defender.y - 20, z: defender.behind(15), scale: 2, opacity: 0, time: 800 },
    'linear',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Alias-target ports (PS defines these via
 *   BattleMoveAnims['x'] = { anim: BattleMoveAnims['y'].anim }
 * so the alias replays the target's body. We port the target body once and alias.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `flashcannon` — a beam of light/water wisps converges on the defender. */
const flashcannon: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
};

/** PS `technoblast` — a charged iceball forms at the user, then lances the defender. */
const technoblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3 },
    { z: attacker.behind(-30), scale: 0.6, opacity: 1, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3, time: 100 },
    { z: attacker.behind(-30), scale: 0.8, opacity: 0.6, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 0.6, opacity: 0.8, time: 400 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 1, opacity: 0.8, time: 800 },
    'accel',
    'explode',
  );

  defender.delay(800);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `spark` — a building electroball + a contact lunge. */
const spark: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  contactattack(scene, actors);
};

/** PS `bulletseed` — two seed orbs pelt the defender. */
const bulletseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.6, time: 30 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 330 },
    'linear',
    'fade',
  );
};

/** PS `jumpkick` — a foot stamp then a contact lunge. */
const jumpkick: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  kick(scene, actors);
  contactattack(scene, actors);
};

/* ──────────────────────────────────────────────────────────────────────────
 * Batch 5 move scripts.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `megadrain` — a green wash + the energy-siphon drain template. */
const megadrain: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#9AB440', 900, 0.2);
  drain(scene, actors);
};

/** PS `megakick` → `jumpkick` body. */
const megakick: MoveAnim = jumpkick;

/** PS `megahorn` — a brown wash, two wisp bursts, and a hard horn ram. */
const megahorn: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#987058', 400, 0.3);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 300 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/**
 * PS `memento` — the user's sprite stretches up and fades. PS drew the user sprite
 * twice via `showEffect(attacker.sp, …)`; our engine has no sprite-as-effect, so
 * those two calls are omitted and the black sacrificial wash is kept.
 */
const memento: MoveAnim = (scene: FxScene) => {
  scene.backgroundEffect('#000000', 900, 0.2);
  // PS drew two attacker-sprite stretches here (showEffect(attacker.sp, …)) — omitted.
};

/** PS `metalclaw` → `clawattack` template. */
const metalclaw: MoveAnim = clawattack;

/** PS `metalsound` → `sound` template. */
const metalsound: MoveAnim = sound;

/** PS `metalburst` / `mirrorcoat` / `mirrorshot` → `flashcannon` body. */
const metalburst: MoveAnim = flashcannon;
const mirrorcoat: MoveAnim = flashcannon;
const mirrorshot: MoveAnim = flashcannon;

/**
 * PS `meteorbeam` — a charge column rises off the user, then a fan of fire/electro
 * orbs streams onto the defender. PS's remote `bg-space.jpg` is replaced with a
 * deep-blue wash.
 */
const meteorbeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xstep = (defender.x - attacker.x) / 5;
  const ystep = (defender.x - 200 - attacker.x) / 5;
  const zstep = (defender.z - attacker.z) / 5;

  // PS used a remote bg-space.jpg here; substitute a deep-space blue wash.
  scene.backgroundEffect('#101038', 900, 0.6);

  for (let i = 0; i < 5; i++) {
    scene.showEffect(
      'mudwisp',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + 200 + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 0.7,
        opacity: 0.6,
        time: 40 * i + 300,
      },
      { opacity: 0, time: 100 * i + 500 },
      'linear',
    );
  }

  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.75, opacity: 0.6 },
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 1.25, opacity: 0, time: 200 },
    'decel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6 },
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 1.5, opacity: 0, time: 200 },
    'decel',
  );

  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 300 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 375 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 425 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 625 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 450 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 650 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 500 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 700 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 575 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 775 },
    'linear',
    'explode',
  );
};

/**
 * PS `meteormash` — a steel-fist punch in space. PS drew two attacker-sprite
 * streaks via `showEffect(attacker.sp, …)`; our engine has no sprite-as-effect, so
 * those are omitted (the punchattack lunge below preserves the read). The remote
 * `bg-space.jpg` is replaced with a deep-space blue wash.
 */
const meteormash: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  // PS used a remote bg-space.jpg here; substitute a deep-space blue wash.
  scene.backgroundEffect('#101038', 1000, 0.4);
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)) — omitted.

  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 450 },
    { scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 500 },
    { y: defender.y + 100, scale: 1, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 500 },
    { x: defender.x - 60, y: defender.y - 80, scale: 1, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 500 },
    { x: defender.x + 60, y: defender.y - 80, scale: 1, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 500 },
    { x: defender.x - 90, y: defender.y + 40, scale: 1, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 500 },
    { x: defender.x + 90, y: defender.y + 40, scale: 1, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  punchattack(scene, actors);
};

/** PS `metronome` — a wagging finger above the user. */
const metronome: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'pointer',
    { x: attacker.x + 30, y: attacker.y + 30, z: attacker.z, scale: 0.4, opacity: 1 },
    {
      x: attacker.x + 40,
      y: attacker.y + 35,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 200,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'pointer',
    {
      x: attacker.x + 40,
      y: attacker.y + 35,
      z: attacker.z,
      scale: 0.5,
      xscale: 0.3,
      yscale: 0.6,
      opacity: 1,
      time: 200,
    },
    {
      x: attacker.x + 30,
      y: attacker.y + 30,
      scale: 0.4,
      xscale: 0.4,
      yscale: 0.4,
      opacity: 0,
      time: 400,
    },
    'decel',
  );
};

/** PS `milkdrink` → `consume` template. */
const milkdrink: MoveAnim = consume;

/**
 * PS `mimic` — the defender's sprite shrinks and an orb returns to the user. PS
 * drew the defender's sprite via `showEffect(defender.sp, …)`; our engine has no
 * sprite-as-effect, so that call is omitted; the electroball "copy returns" is kept.
 */
const mimic: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS drew the defender's sprite shrinking here (showEffect(defender.sp, …)) — omitted.
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 0.5, time: 300 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.2, time: 600 },
    'accel',
    'fade',
  );
};

/** PS `mindreader` / `miracleeye` — a focusing mistball implodes on the defender. */
const mindreader: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0, scale: 2 },
    { opacity: 1, scale: 0.5 },
    'accel',
    'explode',
  );
};
const miracleeye: MoveAnim = mindreader;

/**
 * PS `minimize` — the user shrinks. PS drew two fading user-sprite ghosts via
 * `showEffect(attacker.sp, …)`; our engine has no sprite-as-effect, so those are
 * omitted; the user's own shrink lunge is preserved verbatim.
 */
const minimize: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  // PS drew two fading attacker-sprite ghosts here (showEffect(attacker.sp, …)) — omitted.
  attacker.anim({ y: attacker.y - 30, scale: 0.25, time: 600 }, 'linear');
  attacker.anim({ time: 300 }, 'accel');
};

/** PS `mirrormove` — no animation. */
const mirrormove: MoveAnim = () => {};

/** PS `mist` — water/wisp puffs billow out around the user in a ring. */
const mist: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'waterwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'waterwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y,
        z: attacker.z + 97 * yf2[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 1.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y,
        z: attacker.z + 97 * yf2[i],
        scale: 1.5,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
  }
};

/** PS `mistball` — a misty iceball gathers, lobs onto the defender, and bursts to wisps. */
const mistball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3 },
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 0.6, opacity: 1, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3, time: 100 },
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 0.8, opacity: 0.6, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 0.6, opacity: 0.8, time: 400 },
    { x: defender.x, y: defender.y, z: defender.behind(0), scale: 1, opacity: 0.8, time: 800 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x - 60, y: defender.y + 80, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x + 60, y: defender.y - 80, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x + 90, y: defender.y + 40, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x - 90, y: defender.y - 40, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x, y: defender.y + 100, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 800 },
    { x: defender.x, y: defender.y - 100, scale: 0.9, opacity: 0, time: 1050 },
    'linear',
    'fade',
  );

  defender.delay(500);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `mistyexplosion` — a pink wash and three fireball blooms off the user, who fades. */
const mistyexplosion: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 700, 0.5);
  scene.showEffect(
    'fireball',
    { x: attacker.x + 40, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.6 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x - 40, y: attacker.y - 20, z: attacker.z, scale: 0, opacity: 0.6, time: 150 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x + 10, y: attacker.y + 20, z: attacker.z, scale: 0, opacity: 0.6, time: 300 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  attacker.delay(450).anim({ scale: 4, time: 400, opacity: 0 }, 'linear');
};

/** PS `mistyterrain` — a pink wash + the small `dance` sway. */
const mistyterrain: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 1000, 0.3);
  dance(scene, actors);
};

/** PS `moonblast` — a moon + misty orbs strike the defender; a moon rises overhead. */
const moonblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a remote bg-space.jpg here; substitute a deep-space blue wash.
  scene.backgroundEffect('#101038', 800, 0.6);
  scene.showEffect(
    'moon',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.9, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.9, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.8, time: 500 },
    'linear',
    'explode',
  );

  scene.showEffect(
    'wisp',
    { x: 0, y: +175, z: +50, scale: 1.5, opacity: 1 },
    { time: 800 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: 0, y: +175, z: +50, scale: 0.5, opacity: 0.8 },
    { time: 800 },
    'accel',
    'fade',
  );

  defender.delay(500);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `moonlight` — a moon rises and motes of light drift up off the user. */
const moonlight: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  // PS used a remote bg-space.jpg here; substitute a deep-space blue wash.
  scene.backgroundEffect('#101038', 800, 0.6);
  scene.showEffect(
    'moon',
    { x: attacker.x, y: attacker.y + 150, z: attacker.z, scale: 1, opacity: 0, time: 200 },
    { opacity: 0.8, time: 900 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x + 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 200 },
    { y: attacker.y + 130, opacity: 0, time: 600 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 300 },
    { y: attacker.y + 130, opacity: 0, time: 700 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x + 20, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 400 },
    { y: attacker.y + 130, opacity: 0, time: 800 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'shine',
    { x: attacker.x - 20, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 500 },
    { y: attacker.y + 130, opacity: 0, time: 900 },
    'accel',
    'fade',
  );

  scene.showEffect(
    'wisp',
    { x: 0, y: +175, z: +50, scale: 1.5, opacity: 1 },
    { time: 700 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: 0, y: +175, z: +50, scale: 0.5, opacity: 0.8 },
    { time: 700 },
    'accel',
    'fade',
  );
};

/** PS `morningsun` — warm motes of light drift up off the user under a sunny wash. */
const morningsun: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  // PS used a remote weather-sunnyday.jpg here; substitute a warm sunlight wash.
  scene.backgroundEffect('#f6e08a', 700, 0.5);
  scene.showEffect(
    'wisp',
    { x: attacker.x + 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 200 },
    { y: attacker.y + 130, opacity: 0, time: 600 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x - 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 300 },
    { y: attacker.y + 130, opacity: 0, time: 700 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x + 20, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 400 },
    { y: attacker.y + 130, opacity: 0, time: 800 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 20, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 0.8, time: 500 },
    { y: attacker.y + 130, opacity: 0, time: 900 },
    'accel',
    'fade',
  );
};

/** PS `mudbomb` / `mudsport` — three mud globs lob onto the defender and splatter. */
const mudbomb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};
const mudsport: MoveAnim = mudbomb;

/** PS `mudshot` / `mudslap` — three mud wisps spray onto the defender. */
const mudshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};
const mudslap: MoveAnim = mudshot;

/** PS `muddywater` — the defender rocks in the swell as a wave of mud washes over. */
const muddywater: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');

  scene.backgroundEffect('#996600', 700, 0.2);
  scene.showEffect(
    'mudwisp',
    { x: attacker.x, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x - 30, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x - 60, y: defender.y, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: attacker.x + 30, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 60, y: defender.y, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `mysticalfire` → `flamethrower` body (ported in showdownMoves.ts). Re-ported here. */
const mysticalfire: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};

/** PS `nastyplot` — dark wisps swirl and two electro orbs sink as the user schemes. */
const nastyplot: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);

  scene.showEffect(
    'wisp',
    { x: attacker.x + 20, y: attacker.y + 30, z: attacker.z, scale: 2, opacity: 1, time: 0 },
    { scale: 1, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 20, y: attacker.y + 30, z: attacker.z, scale: 2, opacity: 1, time: 50 },
    { scale: 1, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 60, z: attacker.z, scale: 2, opacity: 1, time: 100 },
    { scale: 1, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 30, z: attacker.z, scale: 0.5, opacity: 1, time: 300 },
    { x: attacker.x, y: attacker.y + 40, z: attacker.z, scale: 0.25, time: 750 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y + 70,
      z: attacker.z,
      scale: 0.5,
      xscale: 0.25,
      yscale: 0.75,
      opacity: 1,
      time: 300,
    },
    { x: attacker.x, y: attacker.y + 80, z: attacker.z, scale: 0.25, time: 750 },
    'decel',
    'fade',
  );
};

/** PS `naturalgift` / `naturepower` — `naturalgift` aliases `technoblast`; `naturepower` is empty. */
const naturalgift: MoveAnim = technoblast;
const naturepower: MoveAnim = () => {};

/** PS `needlearm` → `punchattack` template. */
const needlearm: MoveAnim = punchattack;

/**
 * PS `nightshade` / `nightmare` — a dark shade looms over the defender. PS drew the
 * user's sprite as the looming shade via `showEffect(attacker.sp, …)`; our engine
 * has no sprite-as-effect, so that call is omitted; the red→black wash is kept.
 */
const nightshade: MoveAnim = (scene: FxScene) => {
  scene.backgroundEffect('#550000', 250, 0.3);
  scene.backgroundEffect('#000000', 250, 0.2, 400);
  // PS drew the attacker's sprite as the looming shade here (showEffect(attacker.sp, …)) — omitted.
};
const nightmare: MoveAnim = nightshade;

/** PS `nightslash` — two slash marks left in a dark wash + a contact lunge. */
const nightslash: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'rightslash',
    { x: defender.x + 5, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x - 5, y: defender.y - 20, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { scale: 3, opacity: 0, time: 900 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `nobleroar` → `sound` template. */
const nobleroar: MoveAnim = sound;

/** PS `nuzzle` → `spark` body. */
const nuzzle: MoveAnim = spark;

/** PS `octazooka` — three black ink wisps spray onto the defender. */
const octazooka: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'blackwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `odorsleuth` → `dance` template. */
const odorsleuth: MoveAnim = dance;

/** PS `ominouswind` — three depths of poison wisps swirl across the defender. */
const ominouswind: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  for (let i = 0; i < 3; i++) {
    scene.showEffect(
      'poisonwisp',
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'poisonwisp',
      {
        x: defender.x - 30,
        y: defender.y + 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'poisonwisp',
      {
        x: defender.x + 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'poisonwisp',
      {
        x: defender.x - 30,
        y: defender.y,
        z: defender.behind(i * 40 - 60),
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 30,
        y: defender.y - 35,
        z: defender.behind(i * 40 - 60),
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `originpulse` — a cyan wash, an expanding ring, and three water wisps strike. */
const originpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#00CCCC', 700, 0.5);
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 6, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `outrage` — a red rage wash, anger marks + flares, and a furious ram. */
const outrage: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a CSS linear-gradient(#390000 30%, #B84038); substitute its first stop.
  scene.backgroundEffect('#390000', 600, 0.6, 400);
  scene.showEffect(
    'angry',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { x: attacker.x - 50, y: attacker.y - 50, scale: 2, opacity: 0, time: 300 },
    'ballistic',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 150 },
    { x: attacker.x + 60, y: attacker.y - 50, scale: 2, opacity: 0, time: 450 },
    'ballistic',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { x: attacker.x + 10, y: attacker.y - 60, scale: 2, opacity: 0, time: 600 },
    'ballistic',
  );

  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 600 },
    { scale: 4, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 800 },
    { scale: 4, opacity: 0, time: 1100 },
    'linear',
  );

  attacker.delay(300);
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-5), time: 300 },
    'accel',
  );
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(580);
  defender.anim({ z: defender.behind(20), time: 200 }, 'decel');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `overheat` — a red wash, four fireball bursts + heat-haze wisps, defender shudders. */
const overheat: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a CSS linear-gradient(#390000 30%, #B84038); substitute its first stop.
  scene.backgroundEffect('#390000', 700, 0.4);
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8 },
    { x: defender.x + 60, y: defender.y + 40, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 75 },
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 150 },
    { x: defender.x - 60, y: defender.y, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 225 },
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x + 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 100 },
    { y: attacker.y + 60, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 200 },
    { y: defender.y + 60, opacity: 0.2, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x + 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 300 },
    { y: attacker.y + 60, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 400 },
    { y: attacker.y + 60, opacity: 0.2, time: 800 },
    'linear',
    'fade',
  );

  defender.delay(200);
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ time: 100 }, 'accel');
};

/** PS `painsplit` — paired wisp pulses bloom on both combatants, evening their pain. */
const painsplit: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 200 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 200 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `payback` → `slam` (= `contactattack`) template. */
const payback: MoveAnim = contactattack;

/** PS `payday` — a six-coin fan of electro orbs scatters across the defender. */
const payday: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.3, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.3, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.3, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.3, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 0.3, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.3, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `perishsong` → `sound` template. */
const perishsong: MoveAnim = sound;

/** PS `petalblizzard` — a pink wash; the defender rocks as a fan of petals scatters. */
const petalblizzard: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 700, 0.3);

  defender.delay(350);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');

  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6 },
    { x: defender.x + 80, y: defender.y + 60, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 75 },
    { x: defender.x + 60, y: defender.y - 60, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 80, y: defender.y - 20, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 225 },
    { x: defender.x - 60, y: defender.y + 20, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 275 },
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 300 },
    { x: defender.x - 60, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
};

/** PS `petaldance` — a pink wash; the user sways, lunges through, and scatters petals + mistballs. */
const petaldance: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 1400, 0.5);
  attacker.anim({ x: attacker.x - 10, time: 100 });
  attacker.anim({ x: attacker.x + 10, time: 200 });
  attacker.anim({ x: attacker.x, time: 100 });
  attacker.anim(
    { x: defender.x, y: defender.y + 50, z: defender.behind(-150), time: 200 },
    'ballistic2',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-100), time: 100 }, 'accel');

  attacker.anim({ z: attacker.z, time: 400 }, 'swing');
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { x: attacker.x - 45, y: attacker.y - 45, scale: 2, opacity: 0, time: 300 },
    'decel',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 150 },
    { x: attacker.x + 50, y: attacker.y - 30, scale: 2, opacity: 0, time: 450 },
    'decel',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 250 },
    { x: attacker.x + 25, y: attacker.y - 60, scale: 2, opacity: 0, time: 550 },
    'decel',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { x: attacker.x - 40, y: attacker.y - 40, scale: 2, opacity: 0, time: 600 },
    'decel',
  );

  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: defender.behind(-100), scale: 0.6, opacity: 1, time: 700 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.8, opacity: 0.6, time: 900 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: defender.behind(-100), scale: 0.7, opacity: 1, time: 775 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.9, opacity: 0.6, time: 975 },
    'ballistic2Under',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: defender.behind(-100), scale: 0.5, opacity: 0.6, time: 850 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.8, opacity: 0.3, time: 1050 },
    'ballistic2',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: defender.behind(-100), scale: 0.6, opacity: 1, time: 925 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.9, opacity: 0.6, time: 1125 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'petal',
    { x: attacker.x, y: attacker.y, z: defender.behind(-100), scale: 0.8, opacity: 1, time: 1000 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.6, time: 1200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'petal',
    {
      x: attacker.x,
      y: attacker.y,
      z: defender.behind(-100),
      scale: 0.8,
      opacity: 0.6,
      time: 1075,
    },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.9, opacity: 0.3, time: 1175 },
    'ballistic2',
    'explode',
  );

  defender.delay(825);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/**
 * PS `phantomforce` — the user vanishes behind/below, then strikes from hiding. PS
 * drew the user's sprite vanishing/reappearing via `showEffect(attacker.sp, …)`;
 * our engine has no sprite-as-effect, so those are omitted and the move plays as a
 * shadowy fade-out lunge + contact strike (the choreography PS pairs it with).
 */
const phantomforce: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  scene.backgroundEffect('#000000', 600, 0.3);
  // PS drew the attacker's sprite vanishing here (showEffect(attacker.sp, …)) — omitted;
  // convey the disappear with a fade-out + reappear lunge.
  attacker.anim({ z: attacker.behind(-30), opacity: 0, time: 300 }, 'accel');
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-30), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 5, z: defender.z, opacity: 1, time: 200 },
    'decel',
  );
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 400 }, 'ballistic2Back');
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.6, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  defender.delay(500);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `pinmissile` → `bulletseed` body. */
const pinmissile: MoveAnim = bulletseed;

/** PS `playnice` → `dance` template. */
const playnice: MoveAnim = dance;

/** PS `playrough` — fists, feet, hearts, and dust as the user roughhouses the defender. */
const playrough: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fist',
    { x: defender.x - 10, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 425 },
    {
      x: defender.x - 15,
      y: defender.y - 10,
      z: defender.behind(15),
      scale: 2,
      opacity: 0,
      time: 725,
    },
    'linear',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x - 5, y: defender.y - 5, z: defender.z, scale: 0.6, opacity: 0.6, time: 450 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.8, time: 550 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 575 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 0.8, time: 675 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.6, time: 600 },
    { x: defender.x + 30, y: defender.y - 25, z: defender.z, scale: 1, opacity: 0.8, time: 700 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 750 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(15), scale: 2, opacity: 0, time: 1100 },
    'linear',
  );

  scene.showEffect(
    'heart',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 450 },
    { x: defender.x - 20, y: defender.y + 30, z: defender.z, scale: 1, opacity: 1, time: 550 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'heart',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 675 },
    { x: defender.x - 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 775 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'heart',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 750 },
    { x: defender.x - 10, y: defender.y + 15, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'linear',
    'explode',
  );

  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.2, time: 750 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 750 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.2, time: 1050 },
    'linear',
    'fade',
  );

  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 350 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 150 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `pluck` → `xattack` template. */
const pluck: MoveAnim = xattack;

/** PS `poisonfang` — two poison wisps spread + the bite snap + a contact lunge. */
const poisonfang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 550 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 900 },
    'linear',
  );

  biteTemplate(scene, actors);
  contactattack(scene, actors);
};

/** PS `poisongas` — three poison wisps waft up around the defender. */
const poisongas: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 10, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 1, time: 0 },
    { x: defender.x + 10, y: defender.y + 20, scale: 0.7, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 30, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 1, time: 100 },
    { x: defender.x - 30, y: defender.y + 20, scale: 0.7, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 1, time: 200 },
    { x: defender.x + 40, y: defender.y + 20, scale: 0.7, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `poisonjab` — a poison-laced jab: two wisps + a wisp lance, attacker stabs in and out. */
const poisonjab: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 2,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-20), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-5 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_5: Record<string, MoveAnim> = {
  megadrain,
  megakick,
  megahorn,
  memento,
  metalburst,
  metalclaw,
  metalsound,
  meteorbeam,
  meteormash,
  metronome,
  milkdrink,
  mimic,
  mindreader,
  minimize,
  miracleeye,
  mirrorcoat,
  mirrormove,
  mirrorshot,
  mist,
  mistball,
  mistyexplosion,
  mistyterrain,
  moonblast,
  moonlight,
  morningsun,
  mudbomb,
  mudshot,
  mudslap,
  mudsport,
  muddywater,
  mysticalfire,
  nastyplot,
  naturalgift,
  naturepower,
  needlearm,
  nightshade,
  nightslash,
  nightmare,
  nobleroar,
  nuzzle,
  octazooka,
  odorsleuth,
  ominouswind,
  originpulse,
  outrage,
  overheat,
  painsplit,
  payday,
  payback,
  perishsong,
  petalblizzard,
  petaldance,
  phantomforce,
  pinmissile,
  playnice,
  playrough,
  pluck,
  poisonfang,
  poisongas,
  poisonjab,
};
